import React, { useMemo, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  BACKTESTING_TIMEFRAMES,
  BacktestingTimeframe,
  LanguageNameSpaces,
  TimeRange,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import useOverviewStatistics from "@shared/features/newMarketplace/hooks/useOverviewStatistics";
import { getNetPnlChange } from "@shared/features/newMarketplace/utils";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import Panel from "common/NewLook/Panel.tsx";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";
import StatStrip from "common/NewLook/StatStrip.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import PnlChart from "features/newMarketplace/NewLook/PnlChart.tsx";
import { PnlPoint } from "features/newMarketplace/NewLook/useNetPnlSeries.ts";
import { PnlValue } from "features/newMarketplace/NewLook/NewLookGroupParts";
import { RiskBars } from "features/newMarketplace/NewLook/NewLookRiskMeter";
import useNewLookRisk from "features/newMarketplace/NewLook/useNewLookRisk";
import { PhoneHeroRanges } from "features/dashboard/PhoneDashboard/PhoneHeroControls.tsx";
import {
  CHART_RANGES_ID,
  GROUP_PNL_HEADLINE_ID,
  GROUP_PNL_PANEL_ID,
} from "testIds";
import { formatDateLabel } from "@shared/utils/date";

const RANGE_TO_TIMEFRAME: Record<string, BacktestingTimeframe> = {
  "1w": 7,
  "1m": 30,
  "3m": 90,
  "6m": 180,
};

/**
 * The group page's performance: its returns and risk as a stat strip, then
 * the accumulated net PnL over the chosen range, whose headline follows the
 * crosshair.
 */
const NewLookOverviewStatistics = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const {
    netPnlPercentSeries,
    timeRange,
    setTimeRange,
    timeRanges,
    groupOverview,
    graphTooltipProps,
  } = useOverviewStatistics({ isMobile });
  const risk = useNewLookRisk(groupOverview?.backtesting_score_breakdown);
  const [hovered, setHovered] = useState<PnlPoint>();

  const points = useMemo(
    () =>
      netPnlPercentSeries
        .filter((point) => Number.isFinite(point.y))
        .map((point) => ({ time: Number(point.rawX), value: point.y })),
    [netPnlPercentSeries]
  );

  if (!groupOverview) return null;

  const change = getNetPnlChange(
    groupOverview,
    RANGE_TO_TIMEFRAME[timeRange],
    displayCurrency
  );
  const statItems = [
    ...BACKTESTING_TIMEFRAMES.slice(1).map((timeframe) => ({
      key: `${timeframe}`,
      label: t("overview.month_pnl", { months: timeframe / 30 }),
      value: (
        <PnlValue
          value={getNetPnlChange(groupOverview, timeframe, displayCurrency)}
          role="figureLarge"
        />
      ),
    })),
    {
      key: "risk",
      label: (
        <RiskLabel>
          {t("table.columns.risk")}
          <RiskBars level={risk.level} color={risk.color} />
        </RiskLabel>
      ),
      value: <span title={risk.label}>{risk.shortLabel ?? "–"}</span>,
    },
  ];

  const rangeTabs = (
    <SegmentTabs<TimeRange>
      options={timeRanges as TimeRange[]}
      value={timeRange}
      onChange={setTimeRange}
      ariaLabel={t("overview.accumulated_net_profit")}
    />
  );
  const phoneRanges = (
    <PhoneHeroRanges<TimeRange>
      options={timeRanges as TimeRange[]}
      value={timeRange}
      onChange={setTimeRange}
      label={t("overview.accumulated_net_profit")}
      surface="panel"
      testId={CHART_RANGES_ID}
    />
  );

  return (
    <>
      <StatStrip items={statItems} />
      <Panel
        title={
          <ChartTitle>
            {t("overview.accumulated_net_profit")}
            <HelperTooltip {...graphTooltipProps} />
          </ChartTitle>
        }
        actions={!isMobile && rangeTabs}
        data-testid={GROUP_PNL_PANEL_ID}
      >
        <Headline data-testid={GROUP_PNL_HEADLINE_ID}>
          <PnlValue
            value={hovered ? hovered.value : change}
            role="figureLarge"
          />
          <Meta>
            {hovered
              ? formatDateLabel(moment.unix(hovered.time), "MMM D, YYYY")
              : t("chart.over_range", { range: timeRange.toUpperCase() })}
          </Meta>
        </Headline>
        {points.length > 1 ? (
          <EdgeToEdge $bleedBottom={!isMobile}>
            <PnlChart
              points={points}
              change={change}
              axes={true}
              edgeToEdge={true}
              hideEndDot={true}
              height={isMobile ? 200 : 220}
              onActiveChange={setHovered}
              ariaLabel={t("chart.aria_label", {
                range: timeRange.toUpperCase(),
              })}
            />
          </EdgeToEdge>
        ) : (
          <Skeleton width="100%" height={isMobile ? 200 : 220} radius={8} />
        )}
        {isMobile && phoneRanges}
      </Panel>
    </>
  );
};

export default NewLookOverviewStatistics;

/** The overview while the group loads, in the shape of its panels. */
export const NewLookOverviewSkeleton = () => (
  <SkeletonStack aria-busy={true}>
    <Skeleton width="100%" height={76} radius={12} />
    <Skeleton width="100%" height={330} radius={12} />
    <Skeleton width="100%" height={120} radius={12} />
  </SkeletonStack>
);

// The chart bleeds out of the panel body's side and bottom padding.
const EdgeToEdge = styled.div<{ $bleedBottom: boolean }>`
  margin: 0 -24px ${(p) => (p.$bleedBottom ? -24 : 0)}px;
`;
const RiskLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
`;
const ChartTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;
const Headline = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 12px;
`;
const Meta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
